import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ws } from '@/lib/ws';
import type { InviteMeta, MemberMeta } from '@shared/protocol';
import { useSession } from '@/components/layout/AppShell';
import { relativeTime } from '@/lib/utils';

export function MembersPage() {
  const { org } = useSession();
  const [members, setMembers] = useState<MemberMeta[]>([]);
  const [invites, setInvites] = useState<InviteMeta[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');

  const canManage = org.role === 'owner' || org.role === 'admin';

  useEffect(() => {
    const offM = ws.on('members.state', (m) => setMembers(m.members));
    const offI = ws.on('invites.state', (m) => setInvites(m.invites));
    const offIR = ws.on('invites.revoked', (m) =>
      setInvites((prev) => prev.filter((i) => i.id !== m.id)),
    );
    const offErr = ws.on('error', (m) => {
      if (m.req_type.startsWith('members.') || m.req_type.startsWith('invites.')) setError(m.message);
    });
    ws.send({ type: 'members.list' });
    ws.send({ type: 'invites.list' });
    return () => {
      offM();
      offI();
      offIR();
      offErr();
    };
  }, []);

  function handleInvite() {
    if (!inviteEmail.includes('@')) return;
    ws.send({ type: 'members.invite', email: inviteEmail.trim(), role: inviteRole });
    setInviteEmail('');
  }

  function handleRoleChange(userId: string, role: 'owner' | 'admin' | 'member') {
    ws.send({ type: 'members.role', user_id: userId, role });
  }

  function handleRemove(userId: string) {
    if (!confirm('Remove this member from the org?')) return;
    ws.send({ type: 'members.remove', user_id: userId });
  }

  function handleRevokeInvite(id: string) {
    ws.send({ type: 'invites.revoke', id });
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Members</h1>
        <p className="text-sm text-muted-foreground">{org.name}</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle>Invite a member</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="role">Role</Label>
                <Select
                  id="role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </Select>
              </div>
              <Button onClick={handleInvite} disabled={!inviteEmail.includes('@')}>
                Send invite
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Current members</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                {canManage && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.user_id}>
                  <TableCell>{m.name ?? '—'}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    {org.role === 'owner' ? (
                      <Select
                        value={m.role}
                        onChange={(e) =>
                          handleRoleChange(m.user_id, e.target.value as 'owner' | 'admin' | 'member')
                        }
                      >
                        <option value="owner">Owner</option>
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                      </Select>
                    ) : (
                      <Badge variant="outline">{m.role}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {relativeTime(m.joined_at)}
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleRemove(m.user_id)}>
                        Remove
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {canManage && invites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending invites</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{i.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{i.role}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(i.expires_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleRevokeInvite(i.id)}>
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
